import { useTheme } from '@/lib/ThemeContext'
import { motion } from 'motion/react'
import { useState, useEffect } from 'react'
import { 
  getAutoSyncSettings, 
  saveAutoSyncSettings, 
  AutoSyncSettings,
  exportAllToJson,
  exportSessionToJson,
  importFromJson,
  clearAllData,
  getAllSessions,
  getHermesGatewayUrl,
  saveHermesGatewayUrl,
} from '@/lib/storage'

export function SettingsView() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [autoSyncInterval, setAutoSyncInterval] = useState(5); // minutes
  const [hermesGatewayUrl, setHermesGatewayUrlState] = useState('http://localhost:20128/v1');
  const [testingConnection, setTestingConnection] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      const settings = await getAutoSyncSettings();
      setAutoSyncEnabled(settings.enabled);
      setAutoSyncInterval(settings.intervalMs / 60000); // convert to minutes
      
      const gatewayUrl = await getHermesGatewayUrl();
      if (gatewayUrl) {
        setHermesGatewayUrlState(gatewayUrl);
      }
    };
    loadSettings();
  }, []);

  const handleAutoSyncToggle = async () => {
    const newEnabled = !autoSyncEnabled;
    setAutoSyncEnabled(newEnabled);
    const settings: AutoSyncSettings = {
      enabled: newEnabled,
      intervalMs: autoSyncInterval * 60000,
    };
    await saveAutoSyncSettings(settings);
  };

  const handleIntervalChange = async (minutes: number) => {
    setAutoSyncInterval(minutes)
    const settings: AutoSyncSettings = {
      enabled: autoSyncEnabled,
      intervalMs: minutes * 60000,
    }
    await saveAutoSyncSettings(settings)
  }

  const handleGatewayUrlChange = async (url: string) => {
    setHermesGatewayUrlState(url);
    await saveHermesGatewayUrl(url);
    showToast('Hermes Gateway URL saved');
  };

  const testGatewayConnection = async () => {
    setTestingConnection(true);
    try {
      const response = await fetch(`${hermesGatewayUrl.replace(/\/v1\/?$/, '')}/health`);
      if (response.ok) {
        const data = await response.json();
        showToast(`Connected! Providers: ${data.providers?.join(', ') || 'unknown'}`);
      } else {
        showToast(`Connection failed: ${response.status}`, 'error');
      }
    } catch (error) {
      showToast(`Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    } finally {
      setTestingConnection(false);
    }
  };

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  const handleExportAll = async () => {
    try {
      const json = await exportAllToJson()
      const blob = new Blob([json], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `one-task-view-backup-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      showToast('All data exported successfully')
    } catch (error) {
      showToast(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error')
    }
  }

  const handleExportSession = async () => {
    const sessions = await getAllSessions()
    if (sessions.length === 0) {
      showToast('No sessions to export', 'error')
      return
    }
    const lastSession = sessions.sort((a, b) => b.updatedAt - a.updatedAt)[0]
    try {
      const json = await exportSessionToJson(lastSession.id)
      const blob = new Blob([json], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `session-${lastSession.title}-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      showToast(`Session "${lastSession.title}" exported`)
    } catch (error) {
      showToast(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error')
    }
  }

  const handleImport = async (file: File) => {
    try {
      const text = await file.text()
      const result = await importFromJson(text)
      if (result.success) {
        showToast('Data imported successfully')
      } else {
        showToast(`Import errors: ${result.errors.join(', ')}`, 'error')
      }
    } catch (error) {
      showToast(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error')
    }
  }

  const handleClearAll = async () => {
    if (!confirm('This will permanently delete ALL data (sessions, tiles, answers, history, sync records). Are you sure?')) {
      return
    }
    if (!confirm('FINAL CONFIRMATION: This cannot be undone. Type "DELETE" to confirm.')) {
      return
    }
    try {
      await clearAllData()
      showToast('All data cleared')
      window.location.reload()
    } catch (error) {
      showToast(`Clear failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error')
    }
  }

  const themeOptions = [
    { value: 'system' as const, label: 'System', description: 'Match OS preference' },
    { value: 'dark' as const, label: 'Dark', description: 'Always dark mode' },
    { value: 'light' as const, label: 'Light', description: 'Always light mode' },
  ];

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-16 items-center justify-between px-6 border-b border-border">
        <h1 className="text-xl font-semibold">Settings</h1>
      </header>

      <main className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Hermes Gateway Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="card"
          >
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Hermes Gateway
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-2">Gateway URL</label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={hermesGatewayUrl}
                    onChange={(e) => handleGatewayUrlChange(e.target.value)}
                    placeholder="https://your-tunnel.ngrok-free.app/v1"
                    className="flex-1 px-3 py-2 rounded-xl border border-border bg-surface text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
                  />
                  <button
                    onClick={testGatewayConnection}
                    disabled={testingConnection}
                    className="btn-secondary whitespace-nowrap"
                  >
                    {testingConnection ? (
                      <>
                        <span className="w-4 h-4 border-2 border-accent-primary border-t-transparent rounded-full animate-spin" />
                        Testing...
                      </>
                    ) : (
                      'Test Connection'
                    )}
                  </button>
                </div>
                <p className="text-xs text-text-muted mt-1">
                  Enter your public tunnel URL (ngrok, Cloudflare Tunnel, Tailscale, etc.). 
                  Must end with <code className="font-mono bg-surface px-1 rounded">/v1</code>. 
                  Local <code className="font-mono bg-surface px-1 rounded">localhost</code> only works on this device.
                </p>
              </div>
              <div className="p-3 rounded-xl bg-surface border border-border text-sm text-text-secondary">
                <strong>Current:</strong> <code className="font-mono text-text-primary ml-2">{hermesGatewayUrl}</code>
              </div>
              <details className="group">
                <summary className="cursor-pointer text-sm text-text-secondary flex items-center gap-2">
                  <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  How to expose your local Hermes Gateway
                </summary>
                <div className="mt-3 space-y-3 text-xs text-text-muted">
                  <div className="p-3 rounded-xl bg-surface border border-border">
                    <strong>Option 1: ngrok (quickest)</strong>
                    <pre className="font-mono text-xs mt-1 overflow-x-auto"><code>ngrok http 20128
# Copy the https URL, add /v1 → paste above</code></pre>
                  </div>
                  <div className="p-3 rounded-xl bg-surface border border-border">
                    <strong>Option 2: Cloudflare Tunnel (persistent, free)</strong>
                    <pre className="font-mono text-xs mt-1 overflow-x-auto"><code>cloudflared tunnel --url http://localhost:20128
# Or create named tunnel in Cloudflare Dashboard</code></pre>
                  </div>
                  <div className="p-3 rounded-xl bg-surface border border-border">
                    <strong>Option 3: Tailscale Funnel (if you use Tailscale)</strong>
                    <pre className="font-mono text-xs mt-1 overflow-x-auto"><code>tailscale funnel 20128
# Gives you a stable *.ts.net URL</code></pre>
                  </div>
                </div>
              </details>
            </div>
          </motion.section>

          {/* Theme Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="card"
          >
            <h2 className="text-lg font-semibold mb-4">Appearance</h2>
            <div className="space-y-3">
              {themeOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center justify-between p-3 rounded-xl border transition-all duration-200 cursor-pointer ${
                    theme === option.value
                      ? 'border-accent-primary/50 bg-accent-primary/5'
                      : 'border-border hover:border-border/50'
                  }`}
                >
                  <div>
                    <div className="font-medium text-text-primary">{option.label}</div>
                    <div className="text-sm text-text-muted">{option.description}</div>
                  </div>
                  <input
                    type="radio"
                    name="theme"
                    value={option.value}
                    checked={theme === option.value}
                    onChange={() => setTheme(option.value)}
                    className="w-4 h-4 accent-accent-primary focus-visible-ring"
                  />
                </label>
              ))}
            </div>
            <div className="mt-4 p-3 rounded-xl bg-surface border border-border text-sm text-text-secondary">
              Current: <span className="font-mono text-text-primary">{resolvedTheme}</span>
            </div>
          </motion.section>

          {/* General Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="card"
          >
            <h2 className="text-lg font-semibold mb-4">General</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-text-primary">Reduced Motion</div>
                  <div className="text-sm text-text-muted">Respect system prefers-reduced-motion setting</div>
                </div>
                <input
                  type="checkbox"
                  defaultChecked
                  className="w-5 h-5 accent-accent-primary focus-visible-ring"
                  disabled
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-text-primary">Auto-save</div>
                  <div className="text-sm text-text-muted">Automatically save progress</div>
                </div>
                <input
                  type="checkbox"
                  defaultChecked
                  className="w-5 h-5 accent-accent-primary focus-visible-ring"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-text-primary">Notifications</div>
                  <div className="text-sm text-text-muted">Show session completion notifications</div>
                </div>
                <input
                  type="checkbox"
                  defaultChecked
                  className="w-5 h-5 accent-accent-primary focus-visible-ring"
                />
              </div>
            </div>
          </motion.section>

          {/* Sync Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.25 }}
            className="card"
          >
            <h2 className="text-lg font-semibold mb-4">Sync & Backup</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-text-primary">Auto-sync with Hermes</div>
                  <div className="text-sm text-text-muted">Automatically sync session changes to Hermes in the background</div>
                </div>
                <button
                  onClick={handleAutoSyncToggle}
                  className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus-visible-ring ${
                    autoSyncEnabled
                      ? 'bg-accent-primary'
                      : 'bg-border'
                  }`}
                  role="switch"
                  aria-checked={autoSyncEnabled}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                      autoSyncEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {autoSyncEnabled && (
                <div className="ml-14 mt-2 space-y-3">
                  <div className="flex items-center gap-3">
                    <label className="text-sm text-text-secondary">Sync interval</label>
                    <select
                      value={autoSyncInterval}
                      onChange={(e) => handleIntervalChange(Number(e.target.value))}
                      className="flex-1 max-w-xs px-3 py-2 rounded-xl border border-border bg-surface text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
                    >
                      <option value={1}>1 minute</option>
                      <option value={5}>5 minutes</option>
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={60}>1 hour</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-text-muted">
                    <svg className="w-4 h-4 flex-shrink-0 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Changes will sync automatically every {autoSyncInterval} minute{autoSyncInterval > 1 ? 's' : ''}</span>
                  </div>
                </div>
              )}

              <div className="pt-2 border-t border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-text-primary">Manual sync</div>
                    <div className="text-sm text-text-muted">Push or pull changes on demand</div>
                  </div>
                  <button className="btn-secondary">
                    Sync Now
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-text-primary">View sync log</div>
                  <div className="text-sm text-text-muted">See history of all sync operations</div>
                </div>
                <button className="btn-secondary">
                  View Log
                </button>
              </div>
            </div>
          </motion.section>

          {/* Data Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="card"
          >
            <h2 className="text-lg font-semibold mb-4">Data & Storage</h2>
            <div className="space-y-3">
              <button onClick={handleExportAll} className="btn-secondary w-full justify-start">
                Export All Data (JSON)
              </button>
              <button onClick={handleExportSession} className="btn-secondary w-full justify-start">
                Export Current Session (JSON)
              </button>
              <div className="pt-2 border-t border-border">
                <label className="block text-sm text-text-muted mb-2">Import Data</label>
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => e.target.files?.[0] && handleImport(e.target.files[0])}
                  className="w-full px-3 py-2 rounded-xl border border-border bg-surface text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
                />
              </div>
              <button onClick={handleClearAll} className="btn-secondary w-full justify-start text-error hover:bg-red-500/10">
                Clear All Data
              </button>
            </div>
          </motion.section>

          {/* About Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
            className="card"
          >
            <h2 className="text-lg font-semibold mb-4">About</h2>
            <div className="space-y-2 text-sm text-text-secondary">
              <div className="flex justify-between">
                <span>Version</span>
                <span className="font-mono text-text-primary">0.1.0</span>
              </div>
              <div className="flex justify-between">
                <span>Build</span>
                <span className="font-mono text-text-primary">Tauri 2 + React 18</span>
              </div>
              <div className="flex justify-between">
                <span>Framework</span>
                <span className="font-mono text-text-primary">Vite + TypeScript</span>
              </div>
            </div>
          </motion.section>
        </div>
      </main>
    {/* Toast */}
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
            toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {toast.message}
        </motion.div>
      )}
    </div>
  );
}